import { useEffect, useState } from "react";
import SimpleModal from "../../../../UI/Modals/SimpleModal/SimpleModal";
import MediaUploadWrap from "../../../../MediaUploadWrap/MediaUploadWrap";
import Button from "../../../../UI/Buttons/Button/Button";
import { useForm } from "react-hook-form";
import { useMarketplaceInfo } from "../../../../../hooks/useMarketplaceInfo";
import {
  PharmacyLicenseImageRequest,
  PharmacyLicenseResponse,
} from "../../../../../types/pharmacyLicense";
import styles from "./RegisterLicenseModal.module.scss";

type Props = {
  show: boolean;
  closeModal: () => void;
  onCreate: (
    data: {
      licenseCode: string;
      licenseUserFirstName: string;
      licenseUserSecondName: string;
      marketplaceId: number;
      branchMarketplaceId: number;
      images: PharmacyLicenseImageRequest[];
    },
    files: File[]
  ) => Promise<boolean>;
  onUpdate?: (
    data: {
      id: number;
      licenseCode: string;
      licenseUserFirstName: string;
      licenseUserSecondName: string;
      marketplaceId: number;
      branchMarketplaceId: number;
      images: PharmacyLicenseImageRequest[];
    },
    files: File[]
  ) => Promise<boolean>;
  initialData?: PharmacyLicenseResponse | null;
};

const RegisterLicenseModal = ({
  show,
  closeModal,
  onCreate,
  onUpdate,
  initialData,
}: Props) => {
  const { branchMarketplaceId, marketplaceId, isReady } = useMarketplaceInfo();
  const [imageList, setImageList] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);

  const isEditMode = !!initialData;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    getValues,
    reset,
    setValue,
  } = useForm({
    mode: "onSubmit",
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (initialData && show) {
      setValue("licenseCode", initialData.licenseCode);
      setValue("firstName", initialData.licenseUserFirstName);
      setValue("lastName", initialData.licenseUserSecondName);
      // Note: Existing images cannot be displayed as File objects
      // Backend will handle keeping existing images if needed
    }
  }, [initialData, show, setValue]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!show) {
      reset();
      setImageList([]);
      setIsSubmitting(false);
    }
  }, [show, reset]);

  const onSubmit = async () => {
    if (!isReady) {
      console.warn("Cannot create/update license: marketplace info not ready");
      return;
    }

    if (imageList.length === 0 && !isEditMode) {
      alert("Molimo dodajte najmanje jednu fotografiju");
      return;
    }

    // In edit mode, allow submission even if form not dirty (user might only change images)
    if (!isEditMode && (!isDirty || !isValid)) {
      return;
    }

    setIsSubmitting(true);

    const values = getValues();

    // Prepare image request data
    const images: PharmacyLicenseImageRequest[] = imageList.map(
      (file, index) => ({
        imageLink: file.name,
        priority: index + 1,
        isCardImage: false,
      })
    );

    let success = false;

    if (isEditMode && onUpdate && initialData) {
      // Update existing license
      const updateData = {
        id: initialData.id,
        licenseCode: values.licenseCode,
        licenseUserFirstName: values.firstName,
        licenseUserSecondName: values.lastName,
        marketplaceId: marketplaceId!,
        branchMarketplaceId: branchMarketplaceId!,
        images,
      };
      success = await onUpdate(updateData, imageList);
    } else {
      // Create new license
      const createData = {
        licenseCode: values.licenseCode,
        licenseUserFirstName: values.firstName,
        licenseUserSecondName: values.lastName,
        marketplaceId: marketplaceId!,
        branchMarketplaceId: branchMarketplaceId!,
        images,
      };
      success = await onCreate(createData, imageList);
    }

    setIsSubmitting(false);

    if (success) {
      closeModal();
    }
  };

  const handleCancel = () => {
    if (isDirty || imageList.length > 0) {
      setIsCancelModalVisible(true);
    } else {
      closeModal();
    }
  };

  const confirmCancellation = () => {
    setIsCancelModalVisible(false);
    closeModal();
  };

  if (!show) {
    return null;
  }

  const modalTitle = isEditMode
    ? "Izmeni farmaceuta"
    : "Registracija novog farmaceuta";
  const submitButtonText = isEditMode
    ? isSubmitting
      ? "Ažuriranje..."
      : "Ažuriraj"
    : isSubmitting
    ? "Kreiranje..."
    : "Kreiraj";

  return (
    <>
      <SimpleModal show={show} className={styles.Modal}>
        <div className={styles.ModalTitle}>{modalTitle}</div>
        <div className={styles.ModalContent}>
          <form className={styles.Form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.FormSection}>
              <label htmlFor="license-code">
                Broj licence <span className={styles.Required}>*</span>
              </label>
              <input
                id="license-code"
                type="text"
                className={styles.Input}
                placeholder="Unesite broj licence"
                {...register("licenseCode", {
                  required: "Broj licence je obavezan",
                  maxLength: {
                    value: 128,
                    message: "Maksimalno 128 karaktera",
                  },
                })}
              />
              {errors.licenseCode && (
                <div className={styles.Error}>
                  {errors.licenseCode.message as string}
                </div>
              )}
            </div>

            <div className={styles.FormSection}>
              <label htmlFor="first-name">
                Ime <span className={styles.Required}>*</span>
              </label>
              <input
                id="first-name"
                type="text"
                className={styles.Input}
                placeholder="Unesite ime"
                {...register("firstName", {
                  required: "Ime je obavezno",
                  maxLength: {
                    value: 128,
                    message: "Maksimalno 128 karaktera",
                  },
                })}
              />
              {errors.firstName && (
                <div className={styles.Error}>
                  {errors.firstName.message as string}
                </div>
              )}
            </div>

            <div className={styles.FormSection}>
              <label htmlFor="last-name">
                Prezime <span className={styles.Required}>*</span>
              </label>
              <input
                id="last-name"
                type="text"
                className={styles.Input}
                placeholder="Unesite prezime"
                {...register("lastName", {
                  required: "Prezime je obavezno",
                  maxLength: {
                    value: 128,
                    message: "Maksimalno 128 karaktera",
                  },
                })}
              />
              {errors.lastName && (
                <div className={styles.Error}>
                  {errors.lastName.message as string}
                </div>
              )}
            </div>

            <div className={styles.FormSection}>
              <label>
                Vaša fotografija<span className={styles.Required}>*</span>
              </label>
              <MediaUploadWrap
                setImageList={setImageList}
                imageList={imageList}
                maxNumber={10}
              />
              {imageList.length === 0 && (
                <div className={styles.HelpText}>
                  Dodajte najmanje jednu Vašu fotografiju (jpg, png)
                </div>
              )}
            </div>

            <div className={styles.ButtonGroup}>
              <Button
                type="button"
                onClick={handleCancel}
                buttonType="red"
                disabled={isSubmitting}
              >
                Otkaži
              </Button>
              <Button
                type="submit"
                buttonType="blue"
                disabled={
                  isSubmitting || (imageList.length === 0 && !isEditMode)
                }
              >
                {submitButtonText}
              </Button>
            </div>
          </form>
        </div>
      </SimpleModal>

      {isCancelModalVisible && (
        <SimpleModal
          show={isCancelModalVisible}
          className={styles.ConfirmModal}
        >
          <div className={styles.ConfirmTitle}>Potvrda otkazivanja</div>
          <div className={styles.ConfirmMessage}>
            Da li ste sigurni da želite da otkažete? Sve izmene će biti
            izgubljene.
          </div>
          <div className={styles.ButtonGroup}>
            <Button
              onClick={() => setIsCancelModalVisible(false)}
              buttonType="blue"
            >
              Ne, nastavi
            </Button>
            <Button onClick={confirmCancellation} buttonType="red">
              Da, otkaži
            </Button>
          </div>
        </SimpleModal>
      )}
    </>
  );
};

export default RegisterLicenseModal;
